/**
 * Hello! If you've been tasked to make changes to this module, please know that the reason
 * for all the complexity is Quovo APIs tendency to suck hard, and I feel sorry for you.
 *
 * The code paths may be hard to trace, so here's a safety pig:
 *                          _
 *  _._ _..._ .-',     _.._(`))
 * '-. `     '  /-._.-'    ',/
 *     )         \            '.
 *    / _    _    |             \
 * |  a    a    /              |
 * \   .-.                     ;
 *    '-('' ).-'       ,'       ;
 *       '-;           |      .'
 *          \           \    /
 *          | 7  .__  _.-\   \
 *          | |  |  ``/  /`  /
 *         /,_|  |   /,_/   /
 *            /,_/      '`-'
 */

import Promise from 'bluebird'
import { observable, computed, action, runInAction } from 'mobx'
import { QuovoLinkedAccount } from 'domain/LinkedAccount'
import { validationContext, required } from 'validx'
import QuovoAPI from 'misc/QuovoAPI'
import MultiFactorAuthState from './MultiFactorAuth/MultiFactorAuthState'
import { getLogo } from 'misc/brokerLogos'

// Good luck adventurer. You're gonna need it.
export default class QuovoProviderState {
  @observable selectedBrokerage = null
  @observable brokerages = []
  @observable searchText = ''
  @observable account = null

  @observable authenticating = false
  @observable fetchingBrokerages = false
  @observable syncing = false
  @observable syncProgress = 0
  @observable error = null
  @observable username = ''
  @observable password = ''

  @observable validation = validationContext()
  @observable multiFactorAuthState = null

  constructor ({ rootStore, addAccount }) {
    this.rootStore = rootStore
    this.addAccount = addAccount
  }

  @computed get quovoAPI () {
    return QuovoAPI.create(this.rootStore.authStore.token)
  }

  linkAccount () {
    return new Promise((resolve) => {
      this.reset()
      this.fetchBrokerages()
      this.done = resolve
    })
  }

  updateAccount (account) {
    return new Promise(async (resolve) => {
      // Resets the dialog state.
      this.done = resolve
      this.reset()
      this.setAccount(account)
      await this.fetchBrokerages()
      this.selectBrokerage(this.brokerages.find((b) => b.id === account.brokerageId))
      this.sync()
    })
  }

  async fetchBrokerages () {
    runInAction(() => {
      this.fetchingBrokerages = true
    })
    // Don't worry, this is cached.
    const brokerages = await this.quovoAPI.getBrokerages()
    runInAction(() => {
      this.brokerages.replace(brokerages.map(b => new Brokerage({ ...b, logo: getLogo(b.id) })))
      this.fetchingBrokerages = false
    })
  }

  @computed get filteredBrokerages () {
    if (!this.searchText) {
      return this.brokerages
    }

    return this.brokerages.filter(
      b => b.name.toLowerCase().indexOf(this.searchText.toLowerCase()) > -1
    )
  }

  @action selectBrokerage (brokerage) {
    this.selectedBrokerage = brokerage
  }

  @action setSearchText (newText) {
    this.searchText = newText
  }

  @action setAccount (account) {
    this.account = account
  }

  @action async authenticate () {
    this.setError('')
    if (!this.validate()) {
      return
    }

    try {
      // Shows the syncing screen.
      this.setSyncing(true)
      if (this.account) {
        // We are updating an account.
        const result = await this.quovoAPI.updateAccount(this.account.id, this.username, this.password)
        this.account.set(result.account, { parse: true })
      } else {
        // We are creating a new account.
        const result = await this.quovoAPI.createAccount(this.selectedBrokerage.id, this.username, this.password)
        const linkedAccount = new QuovoLinkedAccount(result.account, { parse: true })
        this.addAccount(linkedAccount)
        this.setAccount(linkedAccount)
      }

      // We need to create a new sync before polling.
      await this.sync({ initSync: true })
    } catch (e) {
      this.setSyncing(false)
      this.setError(e)
      console.warn('Error while logging in to institution with Quovo:', e)
    }
  }

  close (result = false) {
    if (this.done) {
      this.done(result)
    }
  }

  /**
   * This function is the belly of the beast that is Quovo authentication.
   * Basically, it (optionally) creates a new sync request and short-polls for changes to
   * it. When it's time to collect answers, the syncing loop stops and waits for
   * answers to be submitted.
   *
   * The MultiFactorAuthState is responsible for collecting and submitting answers, and
   * after that the syncing loop starts once more. If the sync status is still 'question' or `has_realtime`
   * is still true, MultiFactorAuthState basically attempts to show the challenges again, but
   * when all the challenges are answered, it short-circuits and attempts to sync again.
   * This might happen a few times until the sync status changes.
   *
   * I am really sorry... - that Quovo's API sucks.
   */
  async sync (syncOpts = {}) {
    const { initSync = false } = syncOpts

    const account = this.account
    this.setSyncing(true)

    // Called recursively.
    const doSync = async () => {
      let sync = await this.quovoAPI.getSyncProgress(account.id).then((data) => data.sync)
      if (sync.status === null) {
        sync = await this.initSync()
      }

      account.set({ status: sync.status })

      // Prompt MFA Questions.
      // This check must come before checking for 'syncing'
      // because Quovo thought it would be fucking smart
      // to set status=syncing when has_realtime is true, even
      // though it should be status=questions. Fucking retards.
      if (sync.status === 'questions' || sync.has_realtime) {
        runInAction(() => {
          if (sync.has_realtime) {
            this.answerRealtimeMFA()
          } else {
            this.answerMFA()
          }
        })
        return
      }

      if (sync.status === 'syncing') {
        // Want to show a different indicator when authenticating.
        if (sync.progress.state !== 'authenticating') {
          this.setSyncProgress(sync.progress.percent * 100)
        }

        // Keep polling.
        await Promise.delay(800) // Thiiiis.. is a magic numbaaah... yes it iiiiis, it's a magic numbaaah..
        return doSync()
      }

      if (sync.status === 'config') {
        // So I think after resolving the issue they need to login again.
        this.setError(sync.config_instructions)
        return
      }

      if (sync.status === 'no portfolio') {
        this.setError('There were no Portfolios found within the account.')
        return
      }

      if (sync.status === 'postponed') {
        // Basically we don't know if its good because
        // Quovo will attempt to sync later, so we just
        // go all female and tell them it's fine even though it isn't.
        this.setSyncProgress(100)
        // Give the user a few seconds to read the message.
        setTimeout(() => {
          this.close(account)
        }, 5000)
      }

      if (sync.status === 'good') {
        this.setSyncProgress(100)
        // Give the user a few seconds to celebrate.
        setTimeout(() => {
          this.close(account)
        }, 2500)
        return
      }

      // This basically means the username/password is wrong.
      if (sync.status === 'login') {
        this.setError('Username and/or password were invalid.')
      }
    }

    try {
      // The caller may choose to attempt a new sync before polling.
      if (initSync) {
        await this.initSync()
      }

      await doSync()
    } catch (err) {
      runInAction(() => {
        this.setSyncing(false)
        this.setError(err)
      })
      console.warn('Error while syncing Quovo account', err)
    } finally {
      this.setSyncing(false)
    }
  }

  /**
   * Requests a new sync for the account.
   */
  async initSync () {
    const sync = await this.quovoAPI.sync(this.account.id).then(d => d.sync)
    this.account.set({ status: sync.status })
    return sync
  }

  /**
   * Resets the state of the dialog.
   * This should bring the dialog in such a state as if it was
   * just created.
   *
   * The reason we don't just reinstantiate is that sometimes we may
   * want to preserve state (e.g. fetched brokerages - Like Tupac, some things will never change)
   */
  @action reset () {
    this.error = null
    this.selectedBrokerage = null
    this.account = null
    this.fetchingBrokerages = false
    this.syncing = false
    this.syncProgress = 0
  }

  @action setSyncing (syncing) {
    this.syncing = syncing
  }

  @action setSyncProgress (progress) {
    this.syncProgress = progress
  }

  @action setError (err) {
    // Express yo self
    this.error = typeof err === 'string'
      ? err
      : err.response
        ? err.response.status < 500
          ? err.response.data.message
          : 'An server error occurred while syncing. Please try again later'
        : err.message
  }

  @action validate () {
    return this.validation.reset().validate(this, {
      username: [required('Username is required')],
      password: [required('Password is required')]
    }).isValid
  }

  async answerRealtimeMFA () {
    await this.displayMFA()
    // Realtime MFA reuses the existing sync.
    await this.sync({ initSync: false })
  }

  async answerMFA () {
    await this.displayMFA()
    // Basic MFA requires that we create a new sync.
    await this.sync({ initSync: true })
  }

  async displayMFA (opts) {
    runInAction('create MFA state', () => {
      this.multiFactorAuthState = new MultiFactorAuthState({
        provider: this,
        ...opts
      })
    })
    await this.multiFactorAuthState.answerChallenge()
    runInAction('nullify MFA state', () => {
      this.multiFactorAuthState = null
    })
  }
}

class Brokerage {
  @observable name
  @observable notes
  // Label to use for username
  @observable username
  // Label to use for password
  @observable password

  constructor (attrs) {
    Object.assign(this, attrs)
  }
}



// WEBPACK FOOTER //
// ./src/client/components/LinkAccountDialog/QuovoProvider/ProviderState.js